import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setUpdateScheduleAction } from "../../app/reducers/schedule-slice";
import { Schedule } from "../../types/schedule/schedule";
import { profileState } from "../../app/reducers/profile-slice";
import theme from "../../style/theme";
import { PencilIcon, DocumentDuplicateIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import moment from "moment";
import { Calendar } from "react-date-range";
import { useRef, useState } from "react";
import { useClickOutside } from "../../utils/click/handle-click";
import { scheduleApi } from "../../api/schedule-api";
import { dateFormat } from "../../utils/date/date-format";

interface Props {
    displaySchedule: Schedule;
    handleClickScheduleDeleteButton: (scheduleId: string) => void;
}

const CalendarPopup = styled.div`
  position: absolute;
  margin-top: 2.5rem;
  z-index: 99999;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.2);
`;

const CalendarTitle = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.palette.primitives.gray900};
  border-bottom: 1px solid ${theme.palette.primitives.gray100};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.palette.primitives.gray400};
  
  &:hover {
    color: ${theme.palette.primitives.gray600};
  }

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 2;
  }
`;

const CalendarFooter = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${theme.palette.primitives.gray100};
  display: flex;
  justify-content: flex-end;
`;

const CopyButton = styled.button`
  padding: 6px 12px;
  background: ${theme.palette.primitives.yellow400};
  border: none;
  border-radius: 6px;
  color: ${theme.palette.primitives.gray900};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${theme.palette.primitives.yellow300};
  }
`;

const Container = styled.div`
  width: 118px;
  height: 133px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 80%;
    
  }
`;

const ScheduleDateContainer = styled.div`
  display: inline-flex;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
  font-weight: 700;
  font-size: 16px;
  line-height: 1;
  color: ${theme.palette.primitives.gray900};
  letter-spacing: -0.3px;
  height: 14px;
`;

const YearSpan = styled.span`
  font-weight: 400;
  font-size: 13px;
  color: ${theme.palette.primitives.gray400};
`;

const ButtonGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const SubButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  background: ${props => props.$variant === 'primary' ? theme.palette.primitives.yellow400 : 'transparent'};
  border: none;
  border-radius: ${props => props.$variant === 'primary' ? '6px' : '4px'};
  font-weight: ${props => props.$variant === 'primary' ? '600' : '500'};
  font-size: ${props => props.$variant === 'primary' ? '0.875rem' : '0.75rem'};
  line-height: 1;
  color: ${props => {
        switch (props.$variant) {
            case 'primary':
                return theme.palette.primitives.gray900;
            case 'danger':
                return theme.palette.primitives.gray300;
            default:
                return theme.palette.primitives.gray300;
        }
    }};
  cursor: pointer;
  padding: ${props => props.$variant === 'primary' ? '8px 12px' : '4px 6px'};
  text-align: center;
  letter-spacing: -0.2px;
  white-space: nowrap;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.$variant === 'primary'
        ? theme.palette.primitives.yellow300
        : 'rgba(0, 0, 0, 0.03)'};
    color: ${props => {
        switch (props.$variant) {
            case 'primary':
                return theme.palette.primitives.gray900;
            case 'danger':
                return theme.palette.primitives.red500;
            default:
                return theme.palette.primitives.blue500;
        }
    }};
  }

  svg {
    width: ${props => props.$variant === 'primary' ? '14px' : '12px'};
    height: ${props => props.$variant === 'primary' ? '14px' : '12px'};
    color: inherit;
    stroke-width: ${props => props.$variant === 'primary' ? '2.5' : '2'};
    flex-shrink: 0;
    display: block;
    
  }
`;

const SelectedDateText = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.palette.primitives.gray600};
  border-top: 1px solid ${theme.palette.primitives.gray100};
`;

const InfoText = styled.div`
  padding: 12px 16px;
  font-size: 13px;
  color: ${theme.palette.primitives.gray400};
  border-top: 1px solid ${theme.palette.primitives.gray100};
  line-height: 1.4;
`;

const getNextWorkDay = (date: Date): Date => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);

    // 토요일(6)이나 일요일(0)인 경우
    while (nextDay.getDay() === 0 || nextDay.getDay() === 6) {
        nextDay.setDate(nextDay.getDate() + 1);
    }

    return nextDay;
};

export default function DashboardDateCard({ displaySchedule, handleClickScheduleDeleteButton }: Props) {
    const dispatch = useAppDispatch();
    const { profile } = useAppSelector(profileState);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const calendarRef = useRef<HTMLDivElement>(null);

    useClickOutside(calendarRef, () => setIsCalendarOpen(false));

    const handleClickScheduleUpdateButton = (updateSchedule: Schedule) => {
        dispatch(setUpdateScheduleAction({ updateSchedule }));
    };

    const handleClickScheduleDuplicateButton = async (schedule: Schedule) => {
        const nextWorkDay = getNextWorkDay(new Date(schedule.dateToWork));
        setSelectedDate(nextWorkDay);
        setIsCalendarOpen(true);
    };

    const handleDateSelect = async (date: Date) => {
        setIsCalendarOpen(false);

        try {
            await scheduleApi.duplicate({
                scheduleId: displaySchedule.id,
                dateToWork: dateFormat(date)
            });
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <Container>
            <ScheduleDateContainer>
                <YearSpan>'{moment(displaySchedule.dateToWork).format("YY ")}</YearSpan>
                {moment(displaySchedule.dateToWork).format("MM/DD(ddd)")}
            </ScheduleDateContainer>

            {(displaySchedule.agencyId === profile?.company.id) && (
                <ButtonGroupContainer>
                    <ActionButton
                        onClick={() => handleClickScheduleUpdateButton(displaySchedule)}
                        $variant="primary"
                    >
                        <span className="icon-wrapper">
                            <PencilIcon />
                        </span>
                        수정하기
                    </ActionButton>
                    <SubButtonContainer>
                        <ActionButton
                            onClick={() => handleClickScheduleDuplicateButton(displaySchedule)}
                        >
                            <span className="icon-wrapper">
                                <DocumentDuplicateIcon />
                            </span>
                            복사
                        </ActionButton>
                        {isCalendarOpen && (
                            <CalendarPopup ref={calendarRef}>
                                <CalendarTitle>
                                    복사할 날짜를 선택해주세요
                                    <CloseButton onClick={() => setIsCalendarOpen(false)}>
                                        <XMarkIcon />
                                    </CloseButton>
                                </CalendarTitle>
                                <Calendar
                                    editableDateInputs={true}
                                    date={selectedDate || getNextWorkDay(new Date())}
                                    onChange={(date) => setSelectedDate(date)}
                                    color={theme.palette.primitives.yellow400}
                                />
                                <InfoText>
                                    물류의 정보가 동일한 물류를 복사하며, 동일한 순서로 프로님(배송원)에게 배정됩니다.
                                </InfoText>
                                <SelectedDateText>
                                    선택된 날짜는 '{moment(selectedDate || getNextWorkDay(new Date())).format("YY MM/DD(ddd)")} 입니다.
                                </SelectedDateText>
                                <CalendarFooter>
                                    <CopyButton
                                        onClick={() => selectedDate && handleDateSelect(selectedDate)}
                                    >
                                        복사하기
                                    </CopyButton>
                                </CalendarFooter>
                            </CalendarPopup>
                        )}
                        <ActionButton
                            onClick={() => handleClickScheduleDeleteButton(displaySchedule.id)}
                            $variant="danger"
                        >
                            <span className="icon-wrapper">
                                <TrashIcon />
                            </span>
                            삭제
                        </ActionButton>
                    </SubButtonContainer>
                </ButtonGroupContainer>
            )}
        </Container>
    );
}
